import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ConfigActivityRow } from "@/api/config-server-stages";
import type { ActivityQuery, ActivityAreasResponse } from "@/api/management";
import {
  useActivityScopeItems,
  useActivitiesScroll,
} from "@/hooks/useManagementLists";
import { LIST_PAGE_SIZE } from "@/lib/config-pagination";
import { useDraftVerdicts } from "./managementDraftContext";
import {
  ActivityLine,
  ActivityDecidedLine,
  type ActivitySelection,
} from "./ActivityGroupedList";
import { LoadMore } from "./Paging";
import { StageLoading } from "./StageLoading";

type Area = ActivityAreasResponse["items"][number];
interface TreeProps {
  projectId: string;
  artifactId: string;
  query: ActivityQuery;
  areas: Area[];
  limit?: number;
  selection?: ActivitySelection;
  onVerdict?: (codes: string[], verdict: "approved" | "out_of_scope") => void;
  onReturn?: (code: string) => void;
  onDetails: (row: ConfigActivityRow) => void;
}
export function ActivityTree(props: TreeProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="flex flex-col gap-2 p-3">
      {props.query.verdict === "pending" && !!props.areas.length && (
        <button
          type="button"
          className="self-end px-3 py-2 text-sm"
          onClick={() => setExpanded((value) => !value)}
        >
          {t(expanded ? "Collapse all" : "Expand all")}
        </button>
      )}
      {props.areas.map((area) => (
        <ActivityAreaNode
          key={`${expanded}:${area.application_area ?? ""}`}
          {...props}
          area={area}
          initiallyOpen={expanded}
        />
      ))}
    </div>
  );
}
export function ActivityAreaNode({
  area,
  initiallyOpen,
  ...props
}: TreeProps & { area: Area; initiallyOpen: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(initiallyOpen);
  const query = { ...props.query, area: area.application_area ?? "" };
  const scopes = useActivityScopeItems(
    props.projectId,
    props.artifactId,
    query,
    open,
    props.limit ?? LIST_PAGE_SIZE,
  );
  return (
    <section className="overflow-hidden rounded-lg border border-[var(--exed-card-border)]">
      <h3 className="m-0">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="flex w-full items-center gap-2 bg-[var(--exed-navy)] px-3 py-3 text-left text-white"
        >
          {open ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <span data-testid="area-header">
            {area.application_area ?? t("Others")}
          </span>
          <span className="ml-auto text-xs">
            {t("{{scopes}} scope items · {{activities}} activities", {
              scopes: area.scope_item_count,
              activities: area.total_codes,
            })}
          </span>
        </button>
      </h3>
      {open && (
        <>
          {scopes.isPending ? (
            <StageLoading variant="panel" />
          ) : scopes.isError && !scopes.data ? (
            <TreeError retry={() => void scopes.refetch()} />
          ) : (
            scopes.data?.pages
              .flatMap((page) => page.items)
              .map((scope) => (
                <ActivityScopeNode
                  key={scope.scope_item_id ?? ""}
                  {...props}
                  query={query}
                  scope={scope}
                  initiallyOpen={false}
                />
              ))
          )}
          {scopes.isError && scopes.data && (
            <TreeError retry={() => void scopes.fetchNextPage()} />
          )}
          <LoadMore
            hasMore={!!scopes.hasNextPage}
            loading={scopes.isFetchingNextPage}
            onLoadMore={() => void scopes.fetchNextPage()}
          />
        </>
      )}
    </section>
  );
}
export function ActivityScopeNode({
  scope,
  initiallyOpen,
  ...props
}: Omit<TreeProps, "areas"> & {
  scope: {
    scope_item_id: string | null;
    scope_item_name?: string | null;
    total_codes: number;
  };
  initiallyOpen: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(initiallyOpen);
  const result = useActivitiesScroll(
    props.projectId,
    props.artifactId,
    { ...props.query, scope_item: scope.scope_item_id ?? "" },
    open,
    props.limit ?? LIST_PAGE_SIZE,
  );
  const verdicts = useDraftVerdicts(props.query.node);
  const rows = (result.data?.pages.flatMap((page) => page.items) ?? []).filter(
    (row) =>
      (verdicts.get(row.activity_code) ?? props.query.verdict) ===
      props.query.verdict,
  );
  return (
    <div>
      <h4 className="m-0">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="flex w-full items-center gap-2 border-t bg-[#fffcf2] px-4 py-2.5 text-left text-sm"
        >
          {open ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <span>{scope.scope_item_id ?? "—"}</span>
          <span>{scope.scope_item_name ?? t("No scope item")}</span>
          <span className="ml-auto text-xs">
            {t("{{count}} activities", { count: scope.total_codes })}
          </span>
        </button>
      </h4>
      {open && (
        <>
          {result.isPending ? (
            <StageLoading variant="panel" />
          ) : result.isError && !result.data ? (
            <TreeError retry={() => void result.refetch()} />
          ) : (
            <ul>
              {rows.map((row) =>
                props.query.verdict === "pending" ? (
                  <ActivityLine
                    key={row.activity_code}
                    row={row}
                    scopeId={scope.scope_item_id ?? ""}
                    checked={
                      props.selection?.isChecked(row.activity_code) ?? false
                    }
                    onToggle={props.selection?.onToggle}
                    onVerdict={props.onVerdict}
                    onDetails={props.onDetails}
                  />
                ) : (
                  <ActivityDecidedLine
                    key={row.activity_code}
                    row={row}
                    onReturn={props.onReturn}
                    onDetails={props.onDetails}
                  />
                ),
              )}
            </ul>
          )}
          {result.isError && result.data && (
            <TreeError retry={() => void result.fetchNextPage()} />
          )}
          <LoadMore
            hasMore={!!result.hasNextPage}
            loading={result.isFetchingNextPage}
            onLoadMore={() => void result.fetchNextPage()}
          />
        </>
      )}
    </div>
  );
}
function TreeError({ retry }: { retry: () => void }) {
  const { t } = useTranslation();
  return (
    <div role="alert" className="p-4">
      {t("Could not load configurations.")}{" "}
      <button type="button" onClick={retry}>
        {t("Retry")}
      </button>
    </div>
  );
}
